import { createLinks, LINKS_TYPE } from './navbarLinks.js';

const MENU_CLASS = 'ds-header__navbar__menu';
const PANEL_CLASS = `${MENU_CLASS}__panel`;
const PANEL_ACTIVE_CLASS = `${PANEL_CLASS}--active`;
const HEADER_CLASS = `${MENU_CLASS}__header`;
const BACK_CLASS = `${MENU_CLASS}__back`;
const TITLE_CLASS = `${MENU_CLASS}__title`;
const CLOSE_CLASS = `${MENU_CLASS}__close`;
const FOOTER_CLASS = `${MENU_CLASS}__footer`;

// The chevron marks a row as tappable rather than as a drill-down, so every row in the sheet carries
// one; Help Centre and Log out are the two the design leaves bare.
const ROWS_WITHOUT_CHEVRON = new Set(['help', 'logout']);

export function hasChildren(link) {
  return Array.isArray(link.children) && link.children.length > 0;
}

// A link that builds its own panels - deeper than one level, or filled on demand - instead of
// declaring its sub-panel as `children`. It receives the menu's panel API once the root panel exists.
function buildsOwnPanels(link) {
  return typeof link.createPanels === 'function';
}

export function hasPanels(link) {
  return hasChildren(link) || buildsOwnPanels(link);
}

function matchedLength(pathname, url) {
  if (typeof url !== 'string' || !url.startsWith('/')) {
    return 0;
  }

  const path = url.endsWith('/') ? url.slice(0, -1) : url;

  if (pathname === path || pathname.startsWith(`${path}/`)) {
    return path.length;
  }

  return 0;
}

// The deepest panel the current page belongs to, or null for the root panel. A child only wins when
// it matches more of the path than any top-level destination does, so a page that is a tab in its
// own right keeps the root panel even where a sub-section row points at it too.
export function findPanelForPath({ links, pathname }) {
  let bestLeaf = 0;
  let bestParent = 0;
  let parentName = null;

  links.forEach(link => {
    if (!hasChildren(link)) {
      bestLeaf = Math.max(bestLeaf, matchedLength(pathname, link.url));
      return;
    }

    const own = matchedLength(pathname, link.url);
    const deepest = link.children.reduce(
      (best, child) => Math.max(best, matchedLength(pathname, child.url)),
      own,
    );

    if (deepest > bestParent) {
      bestParent = deepest;
      parentName = link.name;
    }
  });

  return bestParent > bestLeaf ? parentName : null;
}

// The url of the row standing for the page in view. The longest match wins, so a row for a section
// landing page does not claim a page nested below it that has a row of its own.
function findCurrentUrl({ links, pathname }) {
  const rows = links.flatMap(link => [link, ...(link.children ?? [])]);

  return rows.reduce(
    (best, row) => (matchedLength(pathname, row.url) > matchedLength(pathname, best) ? row.url : best),
    null,
  );
}

// The manager menu puts a chevron on every row; the public menus keep their rows as they were and
// mark only the rows that drill in, unless a row asks for one itself.
function withChevron(link, chevronOnEveryRow) {
  if (ROWS_WITHOUT_CHEVRON.has(link.name)) {
    return link;
  }

  const drillsIn = hasChildren(link) || Boolean(link.options?.panelTarget);

  if (!chevronOnEveryRow && !drillsIn && !link.options?.chevron) {
    return link;
  }

  const options = { ...link.options, chevron: true };

  return hasChildren(link)
    ? { ...link, options: { ...options, panelTarget: link.name } }
    : { ...link, options };
}

function createIconButton({ className, ariaLabel, onClick }) {
  const button = document.createElement('button');
  button.type = 'button';
  button.classList.add(className);
  button.setAttribute('aria-label', ariaLabel);
  button.addEventListener('click', onClick);

  return button;
}

// The sub-panel's own top bar, which the stylesheet pins over the navbar row rather than under it:
// the design puts the back chevron where the logo is and the close where the avatar is, so the row
// the sheet was opened from is replaced, not repeated.
function createPanelHeader({ label, onBack, onClose }) {
  const header = document.createElement('div');
  header.classList.add(HEADER_CLASS);

  header.appendChild(createIconButton({
    className: BACK_CLASS,
    ariaLabel: 'Back',
    onClick: onBack,
  }));

  const title = document.createElement('span');
  title.classList.add(TITLE_CLASS);
  title.appendChild(document.createTextNode(label));
  header.appendChild(title);

  if (onClose) {
    header.appendChild(createIconButton({
      className: CLOSE_CLASS,
      ariaLabel: 'Close menu',
      onClick: onClose,
    }));
  }

  return header;
}

function createPanel(links) {
  const panel = document.createElement('div');
  panel.classList.add(PANEL_CLASS);
  panel.appendChild(createLinks({ links, linksType: LINKS_TYPE.DROPDOWN_NAV }));

  return panel;
}

function createFooter({ label, url }) {
  const footer = document.createElement('div');
  footer.classList.add(FOOTER_CLASS);

  const link = document.createElement('a');
  link.href = url;
  link.classList.add('btn', 'btn-primary');
  link.appendChild(document.createTextNode(label));
  footer.appendChild(link);

  return footer;
}

/**
 * The layered mobile menu that lives inside the avatar dropdown: one root panel of top-level links
 * and one panel per link that declares `children`, each opened by its own row and left by the back
 * chevron of its top bar. A link with `createPanels` adds its own panels through the same API.
 *
 * @param {{
 *   links: Array,
 *   footer: ?{label: string, url: string},
 *   onCloseRequest: ?function(): void,
 *   onPanelChange: ?function(?string): void,
 *   chevronOnEveryRow: ?boolean,
 * }} params
 * @returns {{element: HTMLElement, showPanel: function(?string): void, syncToCurrentPath: function(): void}}
 */
export function createMenuPanels({
  links,
  footer = null,
  onCloseRequest = null,
  onPanelChange = null,
  chevronOnEveryRow = true,
}) {
  const element = document.createElement('div');
  element.classList.add(MENU_CLASS);

  const pathname = window.location.pathname;
  const currentUrl = findCurrentUrl({ links, pathname });
  const currentPanel = findPanelForPath({ links, pathname });

  // The sheet covers the page it was opened from, so without a mark on the row the viewer came in
  // on, a dozen near-identical rows give them nothing to locate themselves by. A drill-down row
  // stands in for its whole panel, so it carries the mark while the page sits inside it.
  const withCurrent = link => {
    const isCurrent = hasChildren(link) ? link.name === currentPanel : link.url === currentUrl;

    return isCurrent ? { ...link, options: { ...link.options, current: true } } : link;
  };

  const toRow = link => withChevron(withCurrent(link), chevronOnEveryRow);

  const rootPanel = createPanel(links.map(toRow));

  if (footer) {
    rootPanel.appendChild(createFooter(footer));
  }

  element.appendChild(rootPanel);

  const panelsByName = new Map();

  // A row can point at a panel that is only added later, so the lookup waits for the click; until
  // then the row is an ordinary link.
  function bindPanelRows(panel) {
    panel.querySelectorAll('[data-panel-target]').forEach(row => {
      row.addEventListener('click', event => {
        if (!panelsByName.has(row.dataset.panelTarget)) {
          return;
        }
        event.preventDefault();
        // eslint-disable-next-line no-use-before-define
        showPanel(row.dataset.panelTarget);
      });
    });
  }

  function addPanel({ name, label, links: panelLinks, parentName = null }) {
    const panel = createPanel(panelLinks.map(toRow));
    panel.prepend(createPanelHeader({
      label,
      // eslint-disable-next-line no-use-before-define
      onBack: () => showPanel(parentName),
      onClose: onCloseRequest,
    }));
    element.appendChild(panel);
    panelsByName.set(name, panel);
    bindPanelRows(panel);

    return panel;
  }

  links.filter(hasChildren).forEach(link => {
    addPanel({ name: link.name, label: link.label, links: link.children });
  });

  links.filter(buildsOwnPanels).forEach(link => {
    link.createPanels({
      element,
      addPanel,
      // eslint-disable-next-line no-use-before-define
      showPanel: name => showPanel(name),
      hasPanel: name => panelsByName.has(name),
    });
  });

  function showPanel(name) {
    rootPanel.classList.toggle(PANEL_ACTIVE_CLASS, !name);
    panelsByName.forEach((panel, panelName) => {
      panel.classList.toggle(PANEL_ACTIVE_CLASS, panelName === name);
    });

    // A sub-panel takes the whole screen, so the sheet itself has to know which level it is showing.
    if (onPanelChange) {
      onPanelChange(panelsByName.has(name) ? name : null);
    }
  }

  bindPanelRows(rootPanel);

  function syncToCurrentPath() {
    showPanel(findPanelForPath({ links, pathname: window.location.pathname }));
  }

  syncToCurrentPath();

  return { element, showPanel, syncToCurrentPath };
}
